import { capture, captureOaiqLeadCreated, getAttributionContext } from "../../lib/analytics";
import { getAnalyticsConsentState } from "../../hooks/useConsent";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Requests the Presenter Playbook (presenter-playbook-v1): the
 * email-gated six-page preparation guide. Used by both the Presenter
 * Archetype result page (personalised — pass `answers`, the server
 * recomputes leaders itself) and the public Guides page (generic —
 * omit `answers`, page 3 shows all four archetype cards).
 *
 * On success, triggers a browser download of the returned PDF and
 * resolves; on failure, throws.
 *
 * InterAct Tracking Foundation milestone (2026-10-09) — sends the
 * visitor's EXPLICIT analytics-consent decision (`getAnalyticsConsentState()`,
 * the SAME `useConsent()` cookie-banner state every other asset's
 * request now sends) on BOTH the personalised and generic path, so
 * `routers/presenter_playbook_routes.py` can record it at acceptance.
 * `null` when the visitor has not yet decided — never inferred from
 * `marketing`/`gclid`/`oppref`/`gaClientId` below. `runId` (pass 7) is
 * the Presenter Archetype quiz's own stable run_id — pass it ONLY for
 * a personalised request (never fabricate one for a generic request;
 * `GuideRequestForm.jsx` never passes it at all).
 *
 * Pass 7 correction: `presenter_playbook_requested` is now published
 * by the BACKEND (`routers/presenter_playbook_routes.py`, at
 * acceptance, keyed on `requestId` — never dependent on the blob/
 * download steps below). The frontend-side event that used to share
 * that name was REMOVED, not kept under a new name — the browser has
 * no additional observable fact here beyond what the backend already
 * confirmed via `res.ok` (triggering `a.click()` opens the browser's
 * own save/open handling, whose outcome this code cannot observe).
 */
export const requestPlaybook = async ({ language, answers, email, name, marketing, requestId, gaClientId, runId, attributionContext }) => {
  const personalized = Array.isArray(answers);
  const attribution = getAttributionContext() || {};
  try {
    const res = await fetch(`${BASE}/api/presenter-playbook/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        name: name || undefined,
        consent: true,
        marketing: !!marketing,
        language,
        answers: personalized ? answers : undefined,
        requestId,
        gaClientId: gaClientId || undefined,
        gclid: attribution.gclid || undefined,
        oppref: attribution.oppref || undefined,
        analyticsConsent: getAnalyticsConsentState(),
        runId: personalized ? (runId || undefined) : undefined,
        // InterAct Tracking Foundation milestone (2026-10-10) — built
        // by the caller via `buildAttributionRequestContext()`; sent
        // as-is (never constructed here, so both the personalised and
        // generic path share the one shared adapter call site).
        attributionContext: attributionContext || undefined,
      }),
    });
    if (!res.ok) {
      capture("presenter_playbook_request_failed", { language, personalized, status: res.status });
      const err = new Error(`playbook_request_failed_${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `InterAct_Presenter_Playbook_${language}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    // `requestId` is already this request's own frontend-generated
    // `crypto.randomUUID()` sent to the backend above — reused
    // unchanged as the OpenAI Pixel's official `event_id` dedup key
    // (lead-created tracking audit, 2026-02).
    captureOaiqLeadCreated(requestId);
    return true;
  } catch (err) {
    if (!err.status) capture("presenter_playbook_request_failed", { language, personalized, status: "network" });
    throw err;
  }
};
